import { useMemo } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ONBOARDING_PHASE_LOCATION } from "@forgepoint/shared";
import { OnboardingGantt } from "@/modules/onboarding/OnboardingGantt";
import { formatDateOnly } from "@/modules/onboarding/onboardingDates";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import { PhaseEditorRow } from "./PhaseEditorRow";
import { ScheduleRangeControls } from "./ScheduleRangeControls";

/**
 * Go-live schedule editor: the schedule-range controls, one editable row per
 * phase with reordering, and a live gantt preview. The preview stays empty
 * until the schedule range is set.
 */
export function ScheduleEditorSection() {
  const plan = useOnboardingStore((state) => state.plan);
  const phaseMap = useOnboardingStore((state) => state.phaseMap);
  const phaseCreateState = useOnboardingStore(
    (state) => state.phaseCreateState,
  );
  const createPhase = useOnboardingStore((state) => state.createPhase);
  const updatePhase = useOnboardingStore((state) => state.updatePhase);

  const phases = useMemo(
    () => [...phaseMap.values()].sort((a, b) => a.order - b.order),
    [phaseMap],
  );

  const handleAddPhase = () => {
    const maxOrder = phases.reduce(
      (max, phase) => Math.max(max, phase.order),
      0,
    );
    createPhase({
      title: "New phase",
      location: ONBOARDING_PHASE_LOCATION.REMOTE,
      startDate: plan?.scheduleStart ?? formatDateOnly(new Date()),
      order: maxOrder + 1,
    });
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const other = phases[index + direction];
    const current = phases[index];
    if (!other) {
      return;
    }
    updatePhase(current.id, { order: other.order });
    updatePhase(other.id, { order: current.order });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Go-live schedule</CardTitle>
        <CardDescription>
          Set the schedule range and go-live date, then add phases. Nothing
          shows on the calendar until the range is set.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScheduleRangeControls />

        <Separator />

        <div className="space-y-2">
          {phases.map((phase, index) => (
            <PhaseEditorRow
              key={phase.id}
              phase={phase}
              isFirst={index === 0}
              isLast={index === phases.length - 1}
              onMoveUp={() => handleMove(index, -1)}
              onMoveDown={() => handleMove(index, 1)}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddPhase}
            disabled={phaseCreateState.isSaving}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add phase
            {phaseCreateState.isSaving && (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            )}
          </Button>
          {phaseCreateState.error && (
            <span className="text-sm text-destructive">
              {phaseCreateState.error}
            </span>
          )}
        </div>

        {plan?.scheduleStart && plan?.scheduleEnd && (
          <>
            <Separator />
            <div>
              <p className="pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Preview
              </p>
              <OnboardingGantt
                phases={phases}
                scheduleStart={plan.scheduleStart}
                scheduleEnd={plan.scheduleEnd}
                goLiveDate={plan.goLiveDate}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
